import React from 'react';
import { styled } from 'linaria/react';
import { usePrice } from '@jetshop/core/hooks/usePrice';
import t from '@jetshop/intl';
import { useContext } from 'react';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { css, cx } from 'linaria';

const Wrapper = styled('div')`
  max-width: 100%;
  flex-wrap: wrap;
  position: relative;

  &.add-bottom-space {
    margin-bottom: 26px;
  }
`;
const NormalPrice = styled('div')``;
const OldPrice = styled('div')`
  text-decoration: line-through;
`;
const NewPrice = styled('div')`
  /* color: black !important; */
`;
const MemberPrice = styled('div')``;
const MemberPriceWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  .old-price {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    text-decoration: none;
    color: black !important;
    font-weight: bold !important;
    text-transform: uppercase;
    text-decoration: none !important;
  }
`;

const separatedPriceStyle = css`
  &.price-value-and-currency {
    display: flex;
    align-items: flex-end;
    gap: 0.3rem;
    color: inherit;

    .currency {
      margin-bottom: 4px;
      margin-left: 0px !important;
      color: inherit;
    }
    .price-value {
      color: inherit;
    }
  }
  &.reverse-order {
    flex-direction: row-reverse;
  }
`;

const getCurrencySymbol = currency => {
  switch (currency) {
    case 'EUR':
      return '€';
    default:
      return 'Kr';
  }
};

const stripEmptyDecimals = value =>
  typeof value === 'string'
    ? value.replace(/([,.]00)(?=\s*[^0-9]|$)/g, '')
    : value;

const SeparatedPrice = ({ price, selectedChannel }) => {
  const currencyBeforePrice =
    selectedChannel?.id === 4 || selectedChannel?.id === 6;

  if (price == null || price === '') return null;

  let valuePart;
  let currencyPart;

  // ponytail: typeof check — isNaN("399") is false and skips currency parsing
  if (typeof price === 'number') {
    valuePart = stripEmptyDecimals(String(price));
    currencyPart = getCurrencySymbol(selectedChannel?.currency?.id);
  } else {
    // currency before (€ 10,00) or after (10,00 kr); allows regular/nbsp spaces
    const match = String(price).match(
      /^(?:([^\d\s,.]+)[\s\u00A0]*)?(\d+(?:[\s\u00A0]\d{3})*(?:[,.]\d+)?)(?:[\s\u00A0]*([^\d\s,.]+))?$/
    );
    valuePart = match?.[2]?.trim() ?? price;
    currencyPart = match?.[1] || match?.[3] || '';
  }

  return (
    <div
      className={cx(
        'price-value-and-currency',
        separatedPriceStyle,
        currencyBeforePrice && 'reverse-order'
      )}
    >
      <span className="price-value">{valuePart}</span>
      <span className="currency">{currencyPart}</span>
    </div>
  );
};

export const Price = ({
  className,
  style,
  negative,
  OnHidePriceComponent,
  fromPrice,
  memberPrice,
  loggedIn,
  fixedPercentage,
  isColors,
  children,
  ...props
}) => {
  const { hasDiscount, formattedPricing, hidePrice } = usePrice(props);
  // Format member price through the same pipeline so variant/store updates re-render
  const memberPriceResult = usePrice({
    price: memberPrice,
    previousPrice: memberPrice
  });
  const discountedProduct = memberPrice?.incVat > 0 || hasDiscount;
  const { selectedChannel } = useContext(ChannelContext);

  const cleanPrices = Object.entries(formattedPricing || {}).reduce(
    (acc, [key, value]) => {
      acc[key] = stripEmptyDecimals(value);
      return acc;
    },
    {}
  );

  const formattedMemberPrice = stripEmptyDecimals(
    memberPriceResult?.formattedPricing?.price
  );

  const adjustedPrices = cleanPrices;
  if (hidePrice) {
    if (OnHidePriceComponent) {
      return <OnHidePriceComponent {...props} />;
    } else {
      return null;
    }
  }

  const currency =
    selectedChannel?.currency?.id === 'EUR'
      ? '€'
      : selectedChannel?.currency?.id === 'DKK'
      ? 'DKK'
      : 'kr';

  if (isColors) {
    if (memberPrice?.incVat > 0) {
      return (
        <MemberPrice className={cx('member-price', fromPrice && 'fr')}>
          {fromPrice && (
            <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
          )}
          <SeparatedPrice
            key={memberPrice?.incVat}
            price={formattedMemberPrice ?? `${memberPrice?.incVat} ${currency}`}
            selectedChannel={selectedChannel}
          />
        </MemberPrice>
      );
    } else {
      return (
        <NormalPrice className={cx('price normal-price', fromPrice && 'fr')}>
          {fromPrice && (
            <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
          )}
          <SeparatedPrice
            price={adjustedPrices?.price}
            selectedChannel={selectedChannel}
          />
        </NormalPrice>
      );
    }
  }

  return (
    <Wrapper
      className={cx(
        className,
        !(memberPrice?.incVat > 0) && 'add-bottom-space'
      )}
      style={style}
      data-flight-price
    >
      {discountedProduct ? (
        <>
          {memberPrice?.incVat > 0 ? (
            <MemberPriceWrapper>
              <MemberPrice className={cx('member-price', fromPrice && 'fr')}>
                {fromPrice && (
                  <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
                )}
                <SeparatedPrice
                  key={memberPrice?.incVat}
                  price={
                    formattedMemberPrice ?? `${memberPrice?.incVat} ${currency}`
                  }
                  selectedChannel={selectedChannel}
                />
                {fixedPercentage > 0 && (
                  <div className="percentage">
                    -{fixedPercentage}
                    <span>%</span>
                  </div>
                )}
              </MemberPrice>
              <OldPrice className={cx('old-price', fromPrice && 'fr')}>
                {t('Not a member ')}
                {fromPrice && (
                  <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
                )}
                <SeparatedPrice
                  price={adjustedPrices?.previousPrice}
                  selectedChannel={selectedChannel}
                />
              </OldPrice>
            </MemberPriceWrapper>
          ) : (
            <>
              <NewPrice className={cx('new-price', fromPrice && 'fr')}>
                {fromPrice && (
                  <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
                )}
                <SeparatedPrice
                  price={adjustedPrices?.price}
                  selectedChannel={selectedChannel}
                />
                {fixedPercentage > 0 && (
                  <div className="percentage">
                    -{fixedPercentage}
                    <span>%</span>
                  </div>
                )}
              </NewPrice>
              <OldPrice className="old-price">
                <SeparatedPrice
                  price={adjustedPrices?.previousPrice}
                  selectedChannel={selectedChannel}
                />
              </OldPrice>
            </>
          )}
        </>
      ) : (
        <NormalPrice className={cx('price normal-price', fromPrice && 'fr')}>
          {fromPrice && (
            <span className={cx(fromPrice && 'fr')}>{t('Fr.')}</span>
          )}
          <SeparatedPrice
            price={adjustedPrices?.price}
            selectedChannel={selectedChannel}
          />
        </NormalPrice>
      )}
      {children}
    </Wrapper>
  );
};
Price.Wrapper = Wrapper;
Price.Normal = NormalPrice;
Price.Old = OldPrice;
Price.New = NewPrice;

//# sourceMappingURL=Price.js.map
